import {
  type EventScheduleRecurrenceDto,
  type ParsedMeetingTimeDto,
  type ParseEventScheduleResponse,
} from "@crowdcoursing/api/openapi";
import { DateTime } from "luxon";

import {
  type CustomRecurrenceConfig,
  normalizeMeetingTimesForSubmit,
  presetToCustomConfig,
  type RecurrenceEndType,
  type RecurrenceFrequency,
  type RecurrencePreset,
  type RecurrenceState,
} from "./AddEventTimesModal.recurrence";
import { type MeetingTimeData } from "./AddEventTimesModal.types";
import { buildMeetingTimeFromParts, getUserTimezone } from "./AddEventTimesModal.utils";

const RECURRENCE_PRESETS: RecurrencePreset[] = ["none", "daily", "weekly", "monthly", "annually", "weekdays", "custom"];

const RECURRENCE_END_TYPES: RecurrenceEndType[] = ["never", "on", "after"];

const RECURRENCE_FREQUENCIES: RecurrenceFrequency[] = ["day", "week", "month", "year"];

export interface MapExplicitMeetingTimesOptions {
  defaultCapacity?: number;
  validSeriesIds?: ReadonlySet<string>;
}

export const parseDateOnly = (date: string): DateTime => {
  return DateTime.fromISO(date, { zone: getUserTimezone() }).startOf("day");
};

export const parseTimeOnDate = (date: string, time: string): DateTime => {
  return DateTime.fromFormat(`${date} ${time}`, "yyyy-MM-dd HH:mm", { zone: getUserTimezone() });
};

export const mapExplicitMeetingTimes = (
  meetingTimes: ParsedMeetingTimeDto[],
  options: MapExplicitMeetingTimesOptions = {},
): MeetingTimeData[] => {
  const { defaultCapacity = 0, validSeriesIds } = options;

  const mapped = meetingTimes.flatMap((meetingTime) => {
    const date = meetingTime.date;
    const startTime = meetingTime.startTime;
    const endTime = meetingTime.endTime;

    if (!date || !startTime || !endTime) {
      return [];
    }

    const parsedDate = parseDateOnly(date);
    const capacity = meetingTime.capacity != null && meetingTime.capacity > 0 ? meetingTime.capacity : defaultCapacity;

    let seriesId = meetingTime.meetingTimeSeriesId ?? undefined;
    if (seriesId && validSeriesIds && !validSeriesIds.has(seriesId)) {
      seriesId = undefined;
    }

    return [
      buildMeetingTimeFromParts({
        capacity,
        date: parsedDate,
        endTime: parseTimeOnDate(date, endTime),
        seriesId,
        startTime: parseTimeOnDate(date, startTime),
      }),
    ];
  });

  return normalizeMeetingTimesForSubmit(mapped, { validSeriesIds });
};

const toRecurrencePreset = (value?: string | null): RecurrencePreset => {
  if (value && RECURRENCE_PRESETS.includes(value as RecurrencePreset)) {
    return value as RecurrencePreset;
  }

  return "none";
};

const toRecurrenceEndType = (value?: string | null): RecurrenceEndType => {
  if (value && RECURRENCE_END_TYPES.includes(value as RecurrenceEndType)) {
    return value as RecurrenceEndType;
  }

  return "never";
};

const toRecurrenceFrequency = (value?: string | null): RecurrenceFrequency => {
  if (value && RECURRENCE_FREQUENCIES.includes(value as RecurrenceFrequency)) {
    return value as RecurrenceFrequency;
  }

  return "week";
};

const hasRecurrenceModifiers = (custom?: EventScheduleRecurrenceDto["custom"]): boolean => {
  if (!custom) {
    return false;
  }

  if ((custom.daysOfWeek?.length ?? 0) > 0) {
    return true;
  }

  if ((custom.interval ?? 0) > 1) {
    return true;
  }

  if (custom.frequency) {
    return true;
  }

  if (custom.endType === "on" && custom.endDate) {
    return true;
  }

  return custom.endType === "after" && (custom.occurrenceCount ?? 0) > 0;
};

const mapCustomDto = (custom: NonNullable<EventScheduleRecurrenceDto["custom"]>): CustomRecurrenceConfig => {
  return {
    daysOfWeek: new Set(custom.daysOfWeek ?? []),
    endDate: custom.endDate ? parseDateOnly(custom.endDate) : null,
    endType: toRecurrenceEndType(custom.endType),
    frequency: toRecurrenceFrequency(custom.frequency),
    interval: custom.interval ?? 1,
    occurrenceCount: custom.occurrenceCount ?? 13,
  };
};

export const mapApiRecurrenceToState = (
  recurrence?: EventScheduleRecurrenceDto | null,
  startDate?: DateTime | null,
): RecurrenceState => {
  const preset = toRecurrencePreset(recurrence?.preset);

  if (preset === "custom" && recurrence?.custom) {
    return { custom: mapCustomDto(recurrence.custom), preset: "custom" };
  }

  const customDto = recurrence?.custom;
  if (customDto && hasRecurrenceModifiers(customDto)) {
    const anchor = startDate ?? DateTime.now();
    const base = presetToCustomConfig(preset, anchor);
    const parsed = mapCustomDto(customDto);

    return {
      custom: {
        daysOfWeek: parsed.daysOfWeek.size > 0 ? parsed.daysOfWeek : base.daysOfWeek,
        endDate: parsed.endType === "on" ? parsed.endDate : base.endDate,
        endType: parsed.endType === "never" ? base.endType : parsed.endType,
        frequency: customDto.frequency ? parsed.frequency : base.frequency,
        interval: customDto.interval && customDto.interval > 0 ? parsed.interval : base.interval,
        occurrenceCount: parsed.endType === "after" ? parsed.occurrenceCount : base.occurrenceCount,
      },
      preset: "custom",
    };
  }

  return { preset };
};

export const mapApiRecurrenceToCustomConfig = (
  recurrence?: EventScheduleRecurrenceDto | null,
): CustomRecurrenceConfig | null => {
  const state = mapApiRecurrenceToState(recurrence);
  return state.custom ?? null;
};

export const hasApplicableSchedule = (response: ParseEventScheduleResponse): boolean => {
  if (response.mode === "explicit") {
    return (response.meetingTimes?.length ?? 0) > 0;
  }

  return Boolean(response.builder?.startDate && (response.builder.timeRanges?.length ?? 0) > 0);
};
